import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useTranslation, languages } from "@/lib/i18n";
import logoImage from "@assets/IMG_1809_1765753775163.webp";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const { t, language, setLanguage } = useTranslation();

  const navItems = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.services"), href: "/services" },
    { label: t("nav.gallery"), href: "/gallery" },
    { label: t("nav.videos"), href: "/videos" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.faq"), href: "/faq" },
    { label: t("nav.contact"), href: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link href="/" data-testid="link-logo">
            <div className="flex items-center gap-2 cursor-pointer">
              <img 
                src={logoImage} 
                alt="Hairtrans Turkey Logo" 
                className="w-24 h-24 lg:w-28 lg:h-28 rounded-full object-cover"
                width={200}
                height={183}
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
            {navItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <span
                  className={`text-sm font-medium transition-colors cursor-pointer ${
                    location === item.href
                      ? isScrolled ? "text-primary" : "text-white"
                      : isScrolled ? "text-muted-foreground hover:text-foreground" : "text-white/80 hover:text-white"
                  }`}
                  data-testid={`link-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </span>
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <div className={`flex items-center gap-1.5 ${isScrolled ? 'text-muted-foreground' : 'text-white/80'}`}>
              <Globe className="w-4 h-4" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as typeof language)}
                className="bg-transparent border-0 text-sm font-medium cursor-pointer outline-none appearance-none pr-4"
                style={{ color: 'inherit' }}
                data-testid="select-language"
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code} data-testid={`option-language-${lang.code}`}>
                    {lang.nativeName}
                  </option>
                ))}
              </select>
            </div>
            <a href="tel:+905324131143" className={`flex items-center gap-2 text-sm font-medium ${isScrolled ? 'text-muted-foreground' : 'text-white/80'}`}>
              <Phone className="w-4 h-4" />
              <span>+90 532 413 1143</span>
            </a>
            <Link href="/contact">
              <Button data-testid="button-free-consultation">
                {t("header.freeConsultation")}
              </Button>
            </Link>
          </div>

          <button
            className={`lg:hidden p-2 ${isScrolled ? 'text-foreground' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="button-mobile-menu"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background border-b border-border" data-testid="nav-mobile">
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <div
                  className={`block py-2 text-base font-medium ${
                    location === item.href ? "text-primary" : "text-muted-foreground"
                  }`}
                  data-testid={`link-mobile-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </div>
              </Link>
            ))}
            <div className="pt-4 border-t border-border space-y-3">
              <div className="flex items-center gap-2 w-full">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as typeof language)}
                  className="flex-1 bg-transparent border border-border rounded-md px-3 py-2 text-sm cursor-pointer outline-none"
                  data-testid="select-language-mobile"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.nativeName}
                    </option>
                  ))}
                </select>
              </div>
              <Link href="/contact">
                <Button className="w-full" data-testid="button-mobile-consultation">
                  {t("header.freeConsultation")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
